<script setup lang="ts">
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import IconQC from '@qicgroupdoha/qic-connect-ds/components/IconQC'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import { computed, inject } from 'vue'
  import { DEFAULT_ICON_NAME } from './FlowCard.constants'
  import { useI18n } from 'vue-i18n'
  import { useLinkTarget } from '@/shared/composables/useLinkTarget'

  interface Props {
    title?: string
    subtitle?: string
    withButton?: boolean
    buttonText?: string
    buttonSymbol?: string
    padding?: string
    titleWrap?: boolean
    href?: string
    withBackButton?: boolean
    maxWidth?: number
  }

  interface Emits {
    (e: 'click'): void
    (e: 'back'): void
  }

  interface Slots {
    'default'(): void
    'header'(): void
    'subtitle'(): void
    'button'(): void
    'footer'(): void
  }

  const props = withDefaults(defineProps<Props>(), {
    withButton: true,
    buttonText: undefined,
    buttonSymbol: DEFAULT_ICON_NAME,
    title: '',
    subtitle: '',
    padding: 'var(--spacing-9)',
    titleWrap: true,
    href: undefined,
    withBackButton: false,
    maxWidth: 544,
  })

  const emit = defineEmits<Emits>()

  defineSlots<Slots>()

  const { t } = useI18n()

  const localeRTL = inject<boolean>('localeRTL')
  const iconName = computed(() => {
    if (props.buttonSymbol === DEFAULT_ICON_NAME && localeRTL) {
      return 'arrow-narrow-left'
    } else {
      return props.buttonSymbol
    }
  })
  const { getLinkTarget } = useLinkTarget()
  const buttonText = computed(() => (props.buttonText ? props.buttonText : t('qlm_proceed')))
</script>

<template>
  <div class="base-card" :style="{ maxWidth: `${maxWidth || 544}px` }">
    <slot name="header" />
    <div v-if="title || $slots.subtitle || withBackButton" class="base-card__header">
      <WrapperQC
        v-if="withBackButton"
        gap="var(--spacing-3)"
        class="back-button-qc"
        @click="emit('back')"
      >
        <IconQC
          class="back-button-qc__icon"
          :name="`arrow-narrow-${localeRTL ? 'right' : 'left'}`"
        />
        <span class="text-small-medium">{{
          $t('qic_download_page.download_block.back.button')
        }}</span>
      </WrapperQC>
      <!-- @vue-ignore -->
      <h1
        v-if="title"
        class="base-card__title"
        :class="{
          'heading-2': !$qicConnect.isMobile,
          'heading-3': $qicConnect.isMobile,
          'base-card__title--wrap': titleWrap,
          'base-card__title--with-back-button': withBackButton,
        }"
      >
        {{ title }}
      </h1>
      <slot v-if="subtitle || $slots.subtitle" name="subtitle">
        <p class="text-regular">{{ subtitle }}</p>
      </slot>
    </div>
    <slot />
    <slot name="button">
      <!-- @vue-ignore -->
      <ButtonQC
        v-if="withButton"
        :icon-after-content="iconName"
        wide
        size="large"
        data-test="proceed-button"
        :href="href"
        :target="href ? getLinkTarget() : undefined"
        :rel="href ? 'noopener noreferrer' : undefined"
        @click="emit('click')"
      >
        {{ buttonText }}
      </ButtonQC>
    </slot>
    <slot name="footer" />
  </div>
</template>

<style lang="scss" scoped>
  @use '@scss/utils.scss';

  .base-card {
    background-color: var(--colorBgSrfPrimary);
    border-radius: var(--radius-8);
    padding: v-bind(padding);
    width: 100%;
    gap: var(--spacing-8);
    display: flex;
    flex-direction: column;

    .base-card__header {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-3);
    }

    .base-card__title {
      text-align: center;
      color: var(--colorTxtSrfPrimary);
      margin: 0 auto;

      &--wrap {
        width: 90%;
      }

      &--with-back-button {
        text-align: start;
        margin: 0;
      }
    }

    @media (max-width: 768px) {
      padding: var(--spacing-9) var(--spacing-5);

      .base-card__title {
        width: 90%;
      }
    }
  }

  .back-button-qc {
    cursor: pointer;
    align-items: center;
    color: var(--colorTxtSrfSecondary);
    width: fit-content;
    transition: all 0.2s ease;

    & * {
      transition: all 0.2s ease;
    }

    :deep(.back-button-qc__icon) {
      path {
        fill: var(--colorIcnSrfSecondary);
      }
    }

    &:hover,
    &:active {
      color: var(--colorTxtSrfPrimary);

      :deep(.back-button-qc__icon) {
        path {
          fill: var(--colorIcnSrfPrimary);
        }
      }
    }
  }
</style>
